import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { api, ApiError } from "./api";
import { useI18n, type LangPref } from "./i18n";

export interface Prefs {
  shoppingVoice?: boolean;
  shoppingHelper?: "cat" | "dog";
  quarterSeen?: string;
  community?: { country?: string; city?: string; alias?: string };
  weatherConsent?: boolean;
  /** When this person explicitly agreed to the processing of their health data (GDPR art. 9); null = withdrawn. */
  healthConsent?: string | null;
  locationLabel?: string;
  lastLocation?: { lat: number; lon: number } | null;
  onboarded?: boolean;
  extraReminderCount?: number;
  /** Kitchens this household likes: recipe suggestions show these first. */
  cuisines?: string[];
  /** The home page's "Today" cards, in this person's order, and the ones they hid. */
  homeCards?: HomeCard[];
  homeHidden?: HomeCard[];
}

export const HOME_CARDS = ["health", "budget", "fridge", "shopping"] as const;
export type HomeCard = (typeof HOME_CARDS)[number];

export interface Access {
  plus: boolean;
  /** "family": through the family this person joined (one Plus for the whole family). */
  source: "admin" | "manual" | "subscription" | "trial" | "family" | null;
  trialEndsAt: string | null;
  /** End of Plus given by the owner (last day included); null = no end date. */
  plusUntil?: string | null;
  subscription: { status: string; interval: "month" | "year" | null; periodEnd: string | null; cancelAtPeriodEnd: boolean } | null;
}

export interface Me {
  id: string;
  email: string;
  displayName: string;
  language: LangPref;
  plan: "free" | "plus";
  isAdmin: boolean;
  access: Access;
  prefs: Prefs;
  createdAt: string;
}

export interface AiStatus {
  configured: boolean;
  /** On the trial's smaller allowance (free trial, or subscribed during it and not yet charged). */
  trial?: boolean;
  /** The monthly AI budget is used up; AI is back on the 1st. */
  paused?: boolean;
  used: number;
  limit: number;
  remaining: number;
  /** Food photo estimates today. */
  food: { used: number; limit: number; remaining: number };
  /** Text reads by the small AI when the phone couldn't read a photo (receipts, labels, cards). */
  reads?: { used: number; limit: number; remaining: number };
  /** Spending advice this month. */
  advice?: { used: number; limit: number; remaining: number };
  plans: { trial: number; plus: number; foodPerDay: number; trialFoodPerDay?: number; reads?: number; trialReads?: number; advice?: number };
}

interface AuthValue {
  me: Me | null;
  /** The server couldn't be reached on the last check (offline, or restarting). */
  unreachable: boolean;
  ai: AiStatus | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setAi: (a: AiStatus) => void;
  /** Merge prefs locally and persist them. */
  savePrefs: (p: Partial<Prefs>) => Promise<void>;
  logout: () => Promise<void>;
}

const Ctx = createContext<AuthValue | null>(null);

/** The last known account on this device, so a refresh shows the app at once while it checks with the server. */
const ME_KEY = "nutricart.me";
function cachedMe(): { me: Me; ai: AiStatus | null } | null {
  try {
    return JSON.parse(localStorage.getItem(ME_KEY) ?? "null");
  } catch {
    return null;
  }
}
function rememberMe(v: { me: Me; ai: AiStatus | null } | null) {
  try {
    if (v) localStorage.setItem(ME_KEY, JSON.stringify(v));
    else localStorage.removeItem(ME_KEY);
  } catch {
    /* only a speed-up */
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [cached] = useState(cachedMe);
  const [me, setMe] = useState<Me | null>(cached?.me ?? null);
  const [ai, setAi] = useState<AiStatus | null>(cached?.ai ?? null);
  // With a remembered account there is nothing to wait for: the server check runs in the background.
  const [loading, setLoading] = useState(!cached);
  const [unreachable, setUnreachable] = useState(false);
  const { setPref, pref } = useI18n();

  const refresh = useCallback(async () => {
    try {
      const r = await api.get<{ user: Me | null; ai?: AiStatus }>("/api/auth/me");
      setMe(r.user);
      setAi(r.ai ?? null);
      rememberMe(r.user ? { me: r.user, ai: r.ai ?? null } : null);
      setUnreachable(false);
      // The account's language choice wins on a fresh device.
      if (r.user && r.user.language !== "auto" && r.user.language !== pref) setPref(r.user.language);
    } catch {
      // Offline or the server is restarting (e.g. during a deploy): that is not "signed out".
      // Keep whatever we knew and let the app retry.
      setUnreachable(true);
    } finally {
      setLoading(false);
    }
  }, [pref, setPref]);

  // While the server can't be reached, try again every few seconds and whenever the device comes back online.
  useEffect(() => {
    if (!unreachable) return;
    const timer = window.setInterval(refresh, 4000);
    window.addEventListener("online", refresh);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("online", refresh);
    };
  }, [unreachable, refresh]);

  useEffect(() => {
    refresh();
    const onOut = () => {
      rememberMe(null);
      setMe(null);
    };
    // A paid feature answered 402: the trial or subscription just ended, so reload access and show the paywall.
    const onLocked = () => refresh();
    window.addEventListener("nc:signed-out", onOut);
    window.addEventListener("nc:plus-required", onLocked);
    return () => {
      window.removeEventListener("nc:signed-out", onOut);
      window.removeEventListener("nc:plus-required", onLocked);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePrefs = useCallback(async (p: Partial<Prefs>) => {
    setMe((m) => (m ? { ...m, prefs: { ...m.prefs, ...p } } : m));
    await api.patch("/api/account", { prefs: p });
  }, []);

  const logout = useCallback(async () => {
    // Without a connection the sign-in stays valid on the server and would come back by itself: say so
    // (the caller shows it) instead of only pretending to sign out on this phone.
    await api.post("/api/auth/logout").catch((e) => {
      if (e instanceof ApiError && e.status === 0) throw e;
    });
    rememberMe(null);
    setMe(null);
    setAi(null);
  }, []);

  return <Ctx.Provider value={{ me, ai, loading, unreachable, refresh, setAi, savePrefs, logout }}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth outside provider");
  return v;
}
